import { useGoogleMaps } from '@shared/hooks/useGoogleMaps';
import { useCallback } from 'react';
import { AccommodationListingDTO, MapViewPort } from '../AccommodationsConsole.types';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

/**
 * Sort listings within viewport bounds based on (and priortized by):
 *  1. search score
 *  2. distance from center of viewport
 */
export const useListingSortingByViewport = () => {
  const [googleMaps] = useGoogleMaps();
  const isMobileOrTablet = useIsMobileOrTablet();

  return useCallback(
    (mapViewPort: Maybe<MapViewPort>, listings: AccommodationListingDTO[]) => {
      if (isMobileOrTablet || !mapViewPort || !googleMaps) return listings;

      const { center, bounds } = mapViewPort;
      const centerLatLng = new googleMaps.LatLng(center.lat, center.lng);
      const duplicateLocations = new Set<string>();
      const listingsWithinViewportBounds = listings.flatMap(hotel => {
        const { lat, lng } = hotel.location || {};
        if (lat == null || lng == null) return [];

        if (hotel.joyPlaceId && duplicateLocations.has(hotel.joyPlaceId)) return [];

        const position = new googleMaps.LatLng(lat, lng);
        if (!bounds.contains(position)) return [];

        if (hotel.joyPlaceId) duplicateLocations.add(hotel.joyPlaceId);

        const distance = googleMaps.geometry.spherical.computeDistanceBetween(centerLatLng, position);
        return [{ hotel, distance }];
      });

      // Sort listings within viewport bounds based on couple's pick, search score, and distance.

      // Will need to adjust this based on product needs.
      listingsWithinViewportBounds.sort((a, b) => {
        // 2. If both have search scores, sort by search score
        if (typeof a.hotel.searchScore === 'number' && typeof b.hotel.searchScore === 'number') {
          return b.hotel.searchScore - a.hotel.searchScore; // Sort by search score
        }

        // 3. If one has a search score, prioritize it
        if (typeof a.hotel.searchScore === 'number') return -1; // a has search score, b does not
        if (typeof b.hotel.searchScore === 'number') return 1; // b has search score, a does not

        // 4. If neither has a search score, sort by distance.
        return b.distance - a.distance; // Sort by distance
      });

      return listingsWithinViewportBounds.map(item => item.hotel);
    },
    [googleMaps, isMobileOrTablet],
  );
};
